import { memo, useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { Button } from "ui/components/Button/Button";
import { NumberField } from "ui/components/TextField/NumberField";
import { getEcoFeeSettingsAPI } from "services/api/settings/ecoFee/api";
import { EcoFeeSettings } from "services/api/settings/ecoFee/types";
import { EcoFeeCardProps } from "./types";
import EcoFeeEditCard from "./components/EcoFeeEditCard/EcoFeeEditCard";

const EcoFeeCard: React.FC<EcoFeeCardProps> = () => {
  const { t } = useTranslate();

  const [settings, setSettings] = useState<EcoFeeSettings | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setSettings(await getEcoFeeSettingsAPI());
    } catch {
      return;
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = useCallback(() => {
    fetchSettings();
    setShowEditModal(false);
  }, []);

  const handleEditClose = useCallback(() => {
    setShowEditModal(false);
  }, []);

  // Prazan handler — polja su samo prikaz, uređivanje ide kroz modal.
  const handleNoop = useCallback(() => {}, []);

  if (!settings) return null;

  return (
    <>
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32 border-t border-slate-200 mt-4 pt-4">
        <div className="flex flex-col flex-1 max-w-[300px]">
          <p className="text-base font-bold text-slate-600 py-3">
            Naknada za zaštitu okoliša
          </p>
          <p className="text-sm">
            Stope za izračun naknade Fondu za zaštitu okoliša pri unosu vozila (masa vozila i gume).
          </p>
        </div>
        <div className="flex justify-between items-start flex-1 px-3 my-3">
          <div className="flex flex-col min-w-[500px] gap-3">
            <div>
              <NumberField
                name="perKgRate"
                label="Naknada po masi"
                value={settings.perKgRate}
                onChange={handleNoop}
                groupAppend="€/kg"
                decimals={4}
                cssClassName="bg-white"
                disabled
              />
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <NumberField
                  name="driverMassDeductionKg"
                  label="Odbitak mase – vozač"
                  value={settings.driverMassDeductionKg}
                  onChange={handleNoop}
                  groupAppend="kg"
                  decimals={0}
                  cssClassName="bg-white"
                  disabled
                />
              </div>
              <div className="flex-1">
                <NumberField
                  name="fuelMassDeductionKg"
                  label="Odbitak mase – gorivo"
                  value={settings.fuelMassDeductionKg}
                  onChange={handleNoop}
                  groupAppend="kg"
                  decimals={0}
                  cssClassName="bg-white"
                  disabled
                />
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <NumberField
                  name="tireFeeM1"
                  label="Guma – M1 (osobni)"
                  value={settings.tireFeeM1}
                  onChange={handleNoop}
                  groupAppend="€/guma"
                  decimals={2}
                  cssClassName="bg-white"
                  disabled
                />
              </div>
              <div className="flex-1">
                <NumberField
                  name="tireFeeN1"
                  label="Guma – N1 (dostavni)"
                  value={settings.tireFeeN1}
                  onChange={handleNoop}
                  groupAppend="€/guma"
                  decimals={2}
                  cssClassName="bg-white"
                  disabled
                />
              </div>
            </div>
          </div>
          <div>
            <Button
              variant="outlined"
              children={t("admin.edit")}
              onClick={handleEditClick}
              startIcon="/media/icons/plus.svg"
            />
          </div>
        </div>
      </div>

      <EcoFeeEditCard
        show={showEditModal}
        settings={settings}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
      />
    </>
  );
};

export default memo(EcoFeeCard);
